import { useMemo } from 'react';
import type { RouteAnalyticsView } from '../../../domain/models/contracts';
import { COST_COLORS } from './chartTheme';
import { formatPct, formatUsd } from './formatters';
import { buildCostBreakdown, diagnoseRoute } from './routeMetrics';

interface CostBreakdownProps {
  readonly route: RouteAnalyticsView;
}

const COST_ICONS = {
  fuel: '\u26FD',
  airport: '\u{1F6EC}',
  crewMaintenance: '\u23F1\uFE0F',
  capital: '\u{1F3E2}',
} as const;

/**
 * Where the route's money goes, as shares of fuel, airport fees, crew and maintenance, and capital charge,
 * plus the network feed line and advice for a route that loses money or earns under a 5% margin.
 */
export function CostBreakdown({ route }: CostBreakdownProps) {
  const slices = useMemo(() => buildCostBreakdown(route), [route]);
  const advice = useMemo(() => diagnoseRoute(route), [route]);
  const total = slices.reduce((sum, slice) => sum + slice.usd, 0);
  const feeder = route.feederCreditUSD;

  return (
    <section className="ri-panel" aria-label="Cost breakdown">
      <header className="ri-panel__header">
        <h2 className="ri-panel__title">Cost Breakdown</h2>
        <p className="ri-panel__subtitle">
          Where <strong>{formatUsd(total)}</strong> of daily cost goes
        </p>
      </header>

      {total <= 0 ? (
        <p className="ri-empty">No cost recorded yet. Run the simulation for a day.</p>
      ) : (
        <>
          <div
            className="ri-stack"
            role="img"
            aria-label={slices.map((slice) => `${slice.label} ${formatPct(slice.sharePct, 0)}`).join(', ')}
          >
            {slices.map((slice) =>
              slice.sharePct > 0 ? (
                <span
                  key={slice.key}
                  className="ri-stack__segment"
                  style={{ width: `${slice.sharePct}%`, backgroundColor: COST_COLORS[slice.key] }}
                  title={`${slice.label}: ${formatUsd(slice.usd)}`}
                />
              ) : null,
            )}
          </div>
          <ul className="ri-leaks">
            {slices.map((slice) => (
              <li key={slice.key} className="ri-leak" title={slice.detail}>
                <div className="ri-leak__head">
                  <span className="ri-leak__name">
                    <span className="ri-leak__swatch" style={{ backgroundColor: COST_COLORS[slice.key] }} aria-hidden="true" />
                    <span aria-hidden="true">{COST_ICONS[slice.key]}</span> {slice.label}
                  </span>
                  <span className="ri-leak__figures">
                    {formatUsd(slice.usd)} · {formatPct(slice.sharePct, 0)}
                  </span>
                </div>
                <p className="ri-leak__desc">{slice.detail}</p>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="ri-feed" aria-label="Network feed">
        <h3 className="ri-feed__title">Network feed</h3>
        <dl className="ri-feed__list">
          <div>
            <dt>Connecting revenue on this route</dt>
            <dd>{formatUsd(route.connectingRevenueUSD)}</dd>
          </div>
          <div>
            <dt>{feeder >= 0 ? 'Feeder credit earned' : 'Revenue given to the feeder leg'}</dt>
            <dd>{formatUsd(Math.abs(feeder))}</dd>
          </div>
        </dl>
        <p className="ri-leak__desc">
          A feeder leg is credited with a share of the onward fare of the passengers it brings to a hub.
        </p>
      </div>

      {advice.length > 0 ? (
        <div className="ri-advice" role="note" aria-label="Diagnostic advice">
          <h3 className="ri-advice__title">Why this route is struggling</h3>
          <ul>
            {advice.map((item) => (
              <li key={item.id}>{item.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
